<template>
  <!-- Help centre: search, quick links (FAQs whose answer is just a link), and the questions.
       Answers are formatted by helpers/faq-format.js; long ones open as an article with a contents list. -->
  <section class="faqp">
    <header class="faqp__hero">
      <div class="faqp__hero-top">
        <h1>{{ t('faqPage.title') }}</h1>
        <button v-if="isAdmin" type="button" class="faqp__add" @click="openEditor(null)">+ Add question</button>
      </div>
      <p class="faqp__intro">{{ t('faqPage.intro') }}</p>
      <label class="faqp__search">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
        <input v-model="query" type="search" :placeholder="t('faqPage.search')" :aria-label="t('faqPage.search')" />
      </label>
      <div class="faqp__links">
        <span v-for="link in quickLinks" :key="link.id" class="faqp__link-wrap">
          <a class="faqp__link" :href="link.url" target="_blank" rel="noopener noreferrer"><span class="faqp__dot faqp__dot--tg">➤</span>{{ link.label }}</a>
          <button v-if="isAdmin" type="button" class="faqp__mini" title="Edit this link" @click="openEditor(link.faq)">Edit</button>
        </span>
        <button v-if="!isAdmin" type="button" class="faqp__link" @click="openLiveSupport"><span class="faqp__dot faqp__dot--ok">?</span>{{ t('faqPage.liveSupport') }}</button>
        <a v-if="PARTNERS_ON" class="faqp__link" :href="UNDETECTABLE.url" target="_blank" rel="sponsored noopener"><span class="faqp__dot faqp__dot--partner">U</span>{{ t('faqPage.antidetect') }}</a>
      </div>
    </header>

    <div class="faqp__list-head">
      <h2>{{ t('faqPage.questions') }}</h2>
      <span>{{ query.trim() ? t('faqPage.countOf', { shown: shownCount, n: questions.length }) : t('faqPage.count', { n: questions.length }) }}</span>
    </div>

    <draggable v-model="questions" item-key="id" handle=".faqp__drag" :disabled="!isAdmin" :animation="180" class="faqp__list" @end="saveOrder">
      <template #item="{ element: f }">
        <div v-show="matches(f)" :id="'faq-' + f.id" class="faqp__item" :class="{ 'is-open': isOpen(f.id) }">
          <div class="faqp__summary">
            <span v-if="isAdmin" class="faqp__drag" title="Drag to reorder" aria-hidden="true">⋮⋮</span>
            <button type="button" class="faqp__toggle" :aria-expanded="isOpen(f.id)" :aria-controls="'faq-body-' + f.id" @click="toggle(f.id)">
              <span class="faqp__q">{{ f.question }}</span>
              <span v-if="rendered[f.id].guide" class="faqp__badge">{{ t('faqPage.guide') }}</span>
              <span v-if="rendered[f.id].guide" class="faqp__time">{{ t('faqPage.minRead', { n: rendered[f.id].minutes }) }}</span>
              <span class="faqp__plus" aria-hidden="true"></span>
            </button>
          </div>
          <div v-if="isOpen(f.id)" :id="'faq-body-' + f.id" class="faqp__body">
            <div v-if="rendered[f.id].toc.length >= 3" class="faqp__article">
              <nav class="faqp__toc" :aria-label="t('faqPage.onThisPage')">
                <b>{{ t('faqPage.onThisPage') }}</b>
                <ol>
                  <li v-for="s in rendered[f.id].toc" :key="s.id"><a :href="'#' + s.id" @click.prevent="jump(s.id)">{{ s.title }}</a></li>
                </ol>
              </nav>
              <div class="faq-prose" v-html="rendered[f.id].html"></div>
            </div>
            <div v-else class="faq-prose" v-html="rendered[f.id].html"></div>
            <div v-if="rendered[f.id].guide && !isAdmin" class="faqp__more">
              <span>{{ t('faqPage.stillStuck') }}</span>
              <button type="button" class="faqp__link" @click="openLiveSupport"><span class="faqp__dot faqp__dot--ok">?</span>{{ t('faqPage.liveSupport') }}</button>
            </div>
            <div v-if="isAdmin" class="faqp__admin">
              <button type="button" class="faqp__mini" @click="openEditor(f)">Edit</button>
              <button type="button" class="faqp__mini faqp__mini--danger" @click="remove(f)">Delete</button>
            </div>
          </div>
        </div>
      </template>
    </draggable>
    <p v-if="loaded && !shownCount" class="faqp__empty">{{ t('faqPage.noMatch') }}</p>

    <FaqEditor v-if="isAdmin" v-model="editorOpen" :item="editing" @save="onSave" />
  </section>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue';
import draggable from 'vuedraggable';
import { storeToRefs } from 'pinia';
import { useFAQStore, useAccountStore } from '@/stores';
import { t } from '@/i18n';
import { renderFaq, isGuide, readMinutes, quickLinkOf } from '@/helpers/faq-format';
import FaqEditor from './FaqEditor.vue';
import { UNDETECTABLE, PARTNERS_ON } from '@/helpers/partners';
import { useRoute } from 'vue-router';
import '@/assets/css/faq.css';

const faqStore = useFAQStore();
const { faqs } = storeToRefs(faqStore);
const { user } = storeToRefs(useAccountStore());
const isAdmin = computed(() => user.value?.role === 'admin');
const loaded = ref(false);

// quick links: an FAQ whose whole answer is a link
const quickLinks = computed(() => (faqs.value || []).filter((f) => quickLinkOf(f)).map((f) => ({ id: f.id, url: quickLinkOf(f), label: f.question, faq: f })));
// the questions, in their saved order (draggable for admins)
const questions = computed({
  get: () => (faqs.value || []).filter((f) => !quickLinkOf(f)),
  set: (list) => { faqs.value = [...(faqs.value || []).filter((f) => quickLinkOf(f)), ...list]; },
});
const rendered = computed(() => {
  const out = {};
  for (const f of faqs.value || []) {
    const r = renderFaq(f.answer, 'faq' + f.id);
    out[f.id] = { ...r, guide: isGuide(r.words), minutes: readMinutes(r.words), search: (String(f.question || '') + ' ' + r.plain).toLowerCase().replace(/\s+/g, ' ') };
  }
  return out;
});

// search: not case sensitive; every word typed must appear somewhere in the question or answer
const query = ref('');
const words = computed(() => query.value.toLowerCase().split(/\s+/).filter(Boolean));
const matches = (f) => { const hay = rendered.value[f.id]?.search || ''; return words.value.every((w) => hay.includes(w)); };
const shownCount = computed(() => questions.value.filter(matches).length);

const openIds = ref(new Set());
const isOpen = (id) => openIds.value.has(id);
const toggle = (id) => { const s = new Set(openIds.value); s.has(id) ? s.delete(id) : s.add(id); openIds.value = s; };
const jump = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

const openLiveSupport = () => window.dispatchEvent(new CustomEvent('pp-open-live-support'));

// ---- admin ----
const editorOpen = ref(false);
const editing = ref(null);
const openEditor = (f) => { editing.value = f ? { ...f } : null; editorOpen.value = true; };
const onSave = async ({ id, question, answer }) => {
  const ok = id ? await faqStore.saveFAQ(id, question, answer) : await faqStore.addFAQ({ question, answer, count: (faqs.value || []).length });
  if (ok !== false) editorOpen.value = false;
};
const remove = async (f) => { if (window.confirm(`Delete "${f.question}"?`)) await faqStore.deleteFAQ(f.id); };
const saveOrder = async () => {
  const list = (faqs.value || []).map((f, i) => ({ ...f, order: i + 1 }));
  await faqStore.setOrder(list);
};

onMounted(async () => {
  await faqStore.getAll();
  loaded.value = true;
  openFromHash(window.location.hash);
});
// a link to #faq-<id> opens that question; #undetectable opens the partner question (sidebar "Partners")
const openFromHash = async (hash) => {
  let id = null;
  const m = String(hash || '').match(/^#faq-(\d+)$/);
  if (m) id = Number(m[1]);
  else if (hash === UNDETECTABLE.faqHash) id = (faqs.value || []).find((f) => /undetectable/i.test(f.question || ''))?.id ?? null;
  if (!id) return;
  if (!isOpen(id)) toggle(id);
  await nextTick(); jump('faq-' + id);
};
const route = useRoute();
watch(() => route.hash, (h) => { if (loaded.value) openFromHash(h); });
</script>

<style scoped>
.faqp { max-width: 1080px; margin: 0 auto 32px; color: var(--ui-ink); }
.faqp__hero { display: grid; gap: 14px; padding: 24px 24px 20px; border: 1px solid var(--ui-line); border-radius: 16px; background: var(--ui-card); }
.faqp__hero-top { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.faqp__hero h1 { margin: 0; font-size: 28px; font-weight: 800; color: var(--ui-ink); }
.faqp__intro { margin: 0; max-width: 62ch; color: var(--ui-dim); font-size: 15px; }
.faqp__add { padding: 8px 14px; border: 0; border-radius: 8px; background: var(--ui-pri); color: #ffffff; font-size: 14px; font-weight: 700; cursor: pointer; }
.faqp__search { display: flex; align-items: center; gap: 10px; max-width: 620px; height: 50px; margin: 0; padding: 0 16px; border: 1px solid var(--ui-line-strong); border-radius: 12px; background: var(--ui-page); }
.faqp__search:focus-within { border-color: var(--ui-pri); box-shadow: 0 0 0 3px rgba(101, 96, 240, 0.25); }
.faqp__search svg { flex: none; width: 20px; height: 20px; color: var(--ui-dim); }
.faqp__search input { flex: 1; min-width: 0; border: 0; outline: none; background: transparent; color: var(--ui-ink); font-size: 16px; }
.faqp__search input::placeholder { color: var(--ui-placeholder); }
.faqp__links { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; }
.faqp__link-wrap { display: inline-flex; align-items: center; gap: 6px; }
.faqp__link { display: inline-flex; align-items: center; gap: 8px; padding: 8px 14px; border: 1px solid var(--ui-line-strong); border-radius: 999px; background: var(--ui-card-2); color: var(--ui-ink) !important; font-size: 14px; font-weight: 600; text-decoration: none; cursor: pointer; }
.faqp__link:hover, .faqp__link:focus-visible { border-color: var(--ui-pri); outline: none; }
.faqp__dot { display: grid; place-items: center; width: 22px; height: 22px; border-radius: 50%; color: #ffffff; font-size: 12px; }
.faqp__dot--tg { background: #29a9eb; }
.faqp__dot--ok { background: #1f8f74; }
.faqp__dot--partner { background: #2ec4a0; color: #0b1b16; font-weight: 800; }
.faqp__list-head { display: flex; align-items: baseline; justify-content: space-between; margin: 26px 2px 10px; }
.faqp__list-head h2 { margin: 0; font-size: 18px; font-weight: 700; color: var(--ui-ink); }
.faqp__list-head span { font-size: 13.5px; color: var(--ui-dim); }
.faqp__list { display: grid; gap: 10px; }
.faqp__item { border: 1px solid var(--ui-line); border-radius: 12px; background: var(--ui-card); overflow: hidden; }
.faqp__item.is-open { border-color: var(--ui-line-strong); }
.faqp__summary { display: flex; align-items: center; }
.faqp__drag { flex: none; padding: 0 0 0 14px; color: var(--ui-dim); cursor: grab; letter-spacing: -2px; user-select: none; }
.faqp__toggle { flex: 1; display: flex; align-items: center; gap: 12px; padding: 16px 18px; border: 0; background: transparent; color: var(--ui-ink); text-align: left; cursor: pointer; }
.faqp__toggle:focus-visible { outline: 2px solid var(--ui-pri); outline-offset: -2px; }
.faqp__q { flex: 1; font-size: 16.5px; font-weight: 700; line-height: 1.35; }
.faqp__badge { flex: none; padding: 2px 9px; border-radius: 999px; background: var(--ui-pri-soft); color: var(--ui-ink); font-size: 11.5px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; }
.faqp__time { flex: none; font-size: 12.5px; font-weight: 600; color: var(--ui-dim); }
.faqp__plus { position: relative; flex: none; width: 28px; height: 28px; border: 1px solid var(--ui-line-strong); border-radius: 50%; transition: transform 200ms ease, background-color 200ms ease; }
.faqp__plus::before, .faqp__plus::after { content: ""; position: absolute; left: 50%; top: 50%; width: 12px; height: 2px; margin: -1px 0 0 -6px; border-radius: 2px; background: var(--ui-ink); }
.faqp__plus::after { transform: rotate(90deg); }
.faqp__item.is-open .faqp__plus { transform: rotate(45deg); border-color: var(--ui-pri); background: var(--ui-pri); }
.faqp__item.is-open .faqp__plus::before, .faqp__item.is-open .faqp__plus::after { background: #ffffff; }
@media (max-width: 560px) { .faqp__time { display: none; } .faqp__hero { padding: 18px 16px; } }
.faqp__body { padding: 2px 18px 18px; }
.faqp__article { display: grid; grid-template-columns: 220px minmax(0, 1fr); gap: 28px; }
@media (max-width: 900px) { .faqp__article { grid-template-columns: minmax(0, 1fr); } .faqp__toc { position: static !important; } }
.faqp__toc { position: sticky; top: 90px; align-self: start; padding: 14px; border: 1px solid var(--ui-line); border-radius: 12px; background: var(--ui-page); }
.faqp__toc b { display: block; margin-bottom: 8px; font-size: 12px; letter-spacing: 0.06em; text-transform: uppercase; color: var(--ui-dim); }
.faqp__toc ol { margin: 0; padding: 0; list-style: none; display: grid; gap: 2px; counter-reset: toc; }
.faqp__toc li { counter-increment: toc; }
.faqp__toc a { display: flex; gap: 8px; padding: 5px 6px; border-radius: 6px; color: var(--ui-ink); font-size: 13.5px; line-height: 1.35; text-decoration: none; }
.faqp__toc a::before { content: counter(toc, decimal-leading-zero); flex: none; padding-top: 2px; font-size: 11px; color: var(--ui-dim); font-variant-numeric: tabular-nums; }
.faqp__toc a:hover, .faqp__toc a:focus-visible { background: var(--ui-pri-soft); outline: none; }
.faqp__more { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin-top: 22px; padding: 12px 14px; border: 1px solid var(--ui-line); border-radius: 12px; background: var(--ui-page); font-size: 14px; }
.faqp__admin { display: flex; gap: 8px; margin-top: 14px; padding-top: 12px; border-top: 1px dashed var(--ui-line-strong); }
.faqp__mini { padding: 4px 10px; border: 1px solid var(--ui-line-strong); border-radius: 6px; background: transparent; color: var(--ui-ink); font-size: 12.5px; font-weight: 600; cursor: pointer; }
.faqp__mini:hover { border-color: var(--ui-pri); }
.faqp__mini--danger:hover { border-color: #e0605c; color: #e0605c; }
.faqp__empty { padding: 28px; border: 1px dashed var(--ui-line-strong); border-radius: 12px; text-align: center; color: var(--ui-dim); }
@media (prefers-reduced-motion: reduce) { .faqp__plus { transition: none; } }
</style>
